import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';

import { apikeyListApiKeysOptions } from '@column/api';
import { Button, Dropdown, Icon } from '@column/column-ui-kit';
import {
  Badge,
  DataTable,
  type DataTableColumnDef,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  TextOverflow,
  Timestamp,
} from '@column/ui';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { ApiKeyPreview } from '~/components';
import { LogoLoading } from '~/elements';
import { useNavigate } from '~/lib/navigation';
import { TableTimeFormat } from '~/repositories/UserRepository';
import { useTableConfig } from '~/stores/Config';
import { useSessionStore } from '~/stores/Session';
import { formatDateTime, getFormattedDate } from '~/util';

import { useDeleteAPIKeyAlert } from './_hooks/useDeleteAPIKeyAlert';
import { APIKey } from './utils';

const StyledDropdown = styled(Dropdown)`
  margin: -10px 0;
`;

export const PageApiKeys: React.FC = () => {
  const { currentPlatform, currentUser, isSandbox } = useSessionStore();
  const navigate = useNavigate();
  const { timeFormat } = useTableConfig('api-keys');
  const platformId = currentUser?.defaultPlatformId ?? '';
  const apiKeysQuery = useQuery({
    ...apikeyListApiKeysOptions({
      path: { platform_id: platformId },
    }),
    enabled: !!platformId,
  });
  const apiKeys = apiKeysQuery.data?.api_keys ?? [];
  const isLoading = apiKeysQuery.isLoading;
  const openDeleteAPIKeyAlert = useDeleteAPIKeyAlert({ platformId });
  const headerDecoration = currentPlatform ? (
    <Badge
      variant="outline"
      className={`ml-1 translate-y-px rounded-md ${
        isSandbox
          ? 'border-orange-700/10 bg-orange-200/50 text-orange-600/80 dark:border-orange-400/20 dark:bg-orange-500/15 dark:text-orange-300'
          : 'border-seafoam-700/10 bg-seafoam-100/50 text-seafoam-700 dark:border-seafoam-400/20 dark:bg-seafoam-500/15 dark:text-seafoam-300'
      }`}
    >
      {isSandbox ? 'Sandbox' : 'Live'}
    </Badge>
  ) : null;

  const handleDelete = useCallback(
    (entry: APIKey) => {
      openDeleteAPIKeyAlert(entry);
    },
    [openDeleteAPIKeyAlert]
  );

  const columns = useMemo<DataTableColumnDef<APIKey>[]>(
    () => [
      {
        header: 'Name',
        accessor: 'name',
        width: 'minmax(240px, 1fr)',
        cell: ({ row, value }) => (
          <div className="flex min-w-0 items-center gap-2">
            <span className="shrink-0 truncate font-medium text-foreground">{value}</span>
            {row.description && (
              <TextOverflow className="min-w-0 flex-1 text-xs text-muted-foreground">{row.description}</TextOverflow>
            )}
          </div>
        ),
      },
      {
        header: 'Key',
        accessor: 'api_key_token',
        width: 'minmax(150px, 225px)',
        cell: ({ value }) => <ApiKeyPreview value={value} />,
      },
      {
        header: 'Created',
        accessor: 'created_at',
        width: 'minmax(120px, max-content)',
        cell: ({ value }) =>
          value ? (
            <Timestamp
              date={value}
              align="right"
              className="text-xs text-muted-foreground"
              formatHover={(hoverDate) =>
                formatDateTime(hoverDate, {
                  timeZoneFormat: timeFormat === TableTimeFormat.Relative ? 'local' : 'utc',
                })
              }
            >
              {timeFormat === TableTimeFormat.Relative
                ? getFormattedDate(value, 'time-passed')
                : formatDateTime(value, { timeZoneFormat: 'local' })}
            </Timestamp>
          ) : (
            '-'
          ),
      },
      {
        header: '',
        accessor: 'id',
        width: 'minmax(40px, max-content)',
        className: 'justify-end',
        cell: ({ row }) => (
          <div onClick={(event) => event.stopPropagation()}>
            <StyledDropdown
              isInside
              positionRight
              options={[
                {
                  label: 'Edit',
                  onClick: () => navigate(`${ROUTE.PLATFORM_API_KEYS}/edit/${row.id}`),
                },
                {
                  label: 'Delete',
                  isDanger: true,
                  onClick: () => handleDelete(row),
                },
              ]}
              variant="dots"
            />
          </div>
        ),
      },
    ],
    [handleDelete, navigate, timeFormat]
  );

  return (
    <>
      <PageActions headerDecoration={headerDecoration}>
        <Button onClick={() => navigate(`${ROUTE.PLATFORM_API_KEYS}/create`)} icon={<Icon.Plus />} size="small">
          Create API Key
        </Button>
      </PageActions>

      <div className="px-[var(--page-margin)] pb-6">
        <DataTable<APIKey>
          columns={columns}
          className={!isLoading && apiKeys.length === 0 ? 'min-h-[342px]!' : undefined}
          data={apiKeys}
          isLoading={isLoading}
          loadingContent={<LogoLoading />}
          rowClassName="min-h-11"
          rowKey={(row) => row.id}
          onRowClick={(row) => navigate(`${ROUTE.PLATFORM_API_KEYS}/edit/${row.id}`)}
          emptyContent={
            <Empty className="min-h-[300px]">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <Icon.Key />
                </EmptyMedia>
                <EmptyTitle>No API Keys</EmptyTitle>
                <EmptyDescription>Add your first API key to get started.</EmptyDescription>
              </EmptyHeader>
            </Empty>
          }
        />
      </div>
    </>
  );
};
